import React from "react";
import { InlineMath } from "react-katex";
import FractionInput, { formatLatexPreview } from "../../../../components/inputs/FractionInput";

// --- utils ---
function gcd(a, b) { a=Math.abs(a); b=Math.abs(b); while (b){const t=a%b; a=b; b=t;} return a||1; }
function simplify(p, q) { if (q<0){p=-p; q=-q;} const g=gcd(p,q); return [p/g, q/g]; }
function latexFrac(p, q) { const [pp, qq] = simplify(p, q); return qq===1 ? `${pp}` : `\\tfrac{${pp}}{${qq}}`; }
function dfracLatex(p, q) { const [pp, qq] = simplify(p, q); return qq===1 ? `${pp}` : `\\dfrac{${pp}}{${qq}}`; }

function parseUserFraction(userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw) return null;
  const s = String(userAnswer.raw).replace(",", ".").trim();
  if (/^[+-]?\d+$/.test(s)) return [parseInt(s, 10), 1];
  const m = s.match(/^\s*([+-]?\d+)\s*\/\s*([+-]?\d+)\s*$/);
  if (m) {
    const p = parseInt(m[1], 10), q = parseInt(m[2], 10);
    if (q !== 0) return simplify(p, q);
  }
  return null;
}

const OMEGA = [1,2,3,4,5,6];

function generateOne(variant) {
  const total = 6;

  if (variant === "divisible") {
    const m = Math.random() < 0.5 ? 2 : 3;
    const A = OMEGA.filter(x => x % m === 0);
    const fav = A.length;
    const [p, q] = simplify(fav, total);
    return { variant, params: { m }, fav, total, p, q, A, Omega: OMEGA };
  }

  if (variant === "greater") {
    const k = [2,3,4,5][Math.floor(Math.random()*4)];
    const A = OMEGA.filter(x => x > k);
    const fav = A.length;
    const [p, q] = simplify(fav, total);
    return { variant, params: { k }, fav, total, p, q, A, Omega: OMEGA };
  }

  const k = [2,3,4,5][Math.floor(Math.random()*4)];
  const A = OMEGA.filter(x => x >= k);
  const fav = A.length;
  const [p, q] = simplify(fav, total);
  return { variant: "notLess", params: { k }, fav, total, p, q, A, Omega: OMEGA };
}

export function generateTask(forcedVariant) {
  const pool = ["divisible", "greater", "notLess"];
  const variant = forcedVariant || pool[Math.floor(Math.random()*pool.length)];
  const t = generateOne(variant);
  return {
    type: "DiceSingleRollTask",
    data: t,
    uid: `dice_${variant}_${Date.now()}_${Math.floor(Math.random()*1e9)}`
  };
}

export function renderTask({ data }) {
  return (
    <div style={{ textAlign: "left", margin: "12px 0" }}>
      {data.variant === "divisible" && (
        <>Rzucamy sześcienną kostką. Oblicz prawdopodobieństwo, że liczba wyrzuconych oczek jest podzielna przez <InlineMath math={String(data.params.m)} />:</>
      )}
      {data.variant === "greater" && (
        <>Rzucamy sześcienną kostką. Oblicz prawdopodobieństwo, że liczba wyrzuconych oczek jest większa od <InlineMath math={String(data.params.k)} />:</>
      )}
      {data.variant === "notLess" && (
        <>Rzucamy sześcienną kostką. Oblicz prawdopodobieństwo, że liczba wyrzuconych oczek jest nie mniejsza niż <InlineMath math={String(data.params.k)} />:</>
      )}
    </div>
  );
}

export function renderInput(_task, userAnswer, onChange, disabled) {
  return (
    <div className="flex items-center gap-3">
      <span><InlineMath math="P(A) = " /></span>
      <FractionInput
        value={userAnswer}
        onChange={onChange}
        disabled={disabled}
        label="P(A)"
        placeholder="np. 1/2"
      />
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  const parsed = parseUserFraction(userAnswer);
  if (!parsed) return { correct: false, message: "Podaj ułamek (np. 1/2) lub liczbę całkowitą." };

  const [gp, gq] = parsed;
  const [cp, cq] = simplify(data.p, data.q);
  const [gpp, gqq] = simplify(gp, gq);

  const correct = (gpp === cp) && (gqq === cq);
  return { correct, expected: latexFrac(cp, cq), needsSimplification: !correct && (gp/gq === cp/cq) };
}

export function renderAnswer({ data }) {
  return (
    <div style={{ marginTop: "0.5rem" }}>
      <InlineMath math={`P(A) = ${dfracLatex(data.p, data.q)}`} />
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw) {
    return <i>(brak odpowiedzi)</i>;
  }
  const latex = formatLatexPreview(userAnswer.raw, "P(A)");
  return (
    <div style={{ marginTop: "0.5rem" }}>
      <InlineMath math={latex} />
    </div>
  );
}

export function renderExplanation({ data }) {
  const Omega = Array.isArray(data?.Omega) ? data.Omega : [1,2,3,4,5,6];
  const A     = Array.isArray(data?.A)     ? data.A     : [];

  const rawNum = A.length, rawDen = Omega.length;
  const [simpNum, simpDen] = simplify(rawNum, rawDen);
  const simplifiedTail =
    (simpNum === rawNum && simpDen === rawDen) ? "" : `= \\dfrac{${simpNum}}{${simpDen}}`;

  const probLine =
    `P(\\mathrm{A}) = \\dfrac{|\\mathrm{A}|}{|\\Omega|} = ` +
    `\\dfrac{${rawNum}}{${rawDen}} ${simplifiedTail}`;

  return (
    <div style={{ textAlign: "left", lineHeight: 1.75 }}>
      <div><InlineMath math={`|\\Omega| = ${Omega.length}`} /></div>
      <div><InlineMath math={`|\\mathrm{A}| = ${A.length}`} /></div>
      <div><InlineMath math={probLine} /></div>
    </div>
  );
}
